---
title: "Smart Money Concepts: Liquidity, Order Blocks, Fair Value Gaps"
slug: smart-money-concepts
shortTitle: "Smart Money Concepts"
description: "A balanced guide to smart money concepts: liquidity sweeps, structure breaks, order blocks and fair value gaps, how traders apply them, and their real limits."
level: advanced
category: trading
course: advanced
order: 2
topics: [technical-analysis, trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/smart-money-concepts.svg
faqs:
  - q: "Is smart money concepts a proven strategy?"
    a: "No. It is a popular way of describing price movement, but there is no robust independent evidence that it works better than other approaches. Treat it as a set of ideas to define precisely and test, not as an edge you can assume."
  - q: "What is the difference between an order block and a supply or demand zone?"
    a: "Very little in practice. Both mark the area where a strong move began and expect price to react if it returns. Order block definitions are usually narrower, often a single candle, but they describe the same idea."
  - q: "Do fair value gaps always get filled?"
    a: "No. Some gaps are revisited quickly, some much later, and some never. Others are filled and then broken straight through. A gap is a zone of interest, not a target the price must reach."
  - q: "Are institutions really hunting my stop loss?"
    a: "That cannot be verified from a chart. Stops tend to cluster just beyond obvious highs and lows, so price often reaches them in the normal course of trading. With a CFD, your own stop is not visible in the underlying market's order book."
  - q: "Can I use smart money concepts on {{BRAND}}?"
    a: "Yes, in the sense that the ideas are read from candles, which the trade chart shows on timeframes from 1m to 1D. The chart has no drawing tools, so note your zones as prices and place Limit or Stop orders with TP and SL in the order panel."
---

**Smart money concepts**, often shortened to SMC, are a popular framework for reading charts, especially among crypto and forex traders online. The idea is that large institutions, the "smart money", leave footprints in price as they buy and sell in size, and that you can learn to spot them. The framework comes with its own vocabulary: liquidity, sweeps, breaks of structure, order blocks and fair value gaps.

This lesson explains each term in plain English, shows how traders turn them into a trade, and is honest about the limits. SMC is not proven, its definitions vary between educators, and much of it is older support, resistance and supply-and-demand thinking under new names. Used carefully, though, its ideas can sharpen how you think about stops and entries.

<CTA />

<KeyTakeaways>

- **It is a vocabulary, not a proven edge.** SMC renames and extends older ideas; there is no robust evidence that it works better than other approaches.
- **Liquidity means clustered orders.** Stops and pending orders tend to gather just beyond obvious highs and lows, and price often probes those areas.
- **Order blocks and fair value gaps are zones.** They mark where a strong move began, or where price moved so fast it left an imbalance.
- **Definitions vary.** Two traders can mark different zones on the same chart, so write precise rules and test them before trusting them.

</KeyTakeaways>

## Liquidity and sweeps

In SMC, **liquidity** means orders waiting to be filled. Above an obvious swing high, or a pair of equal highs, sit the stop losses of sellers and the buy stops of breakout traders; this is called **buy-side liquidity**. Below an obvious low sits **sell-side liquidity**.

A **liquidity sweep** (also called a stop run or liquidity grab) is a quick push beyond such a high or low that triggers those orders and then reverses back inside the range. In classic terms, it is a failed breakout.

The practical lesson holds whether or not you adopt the framework: a stop placed exactly at an obvious high or low sits where many other stops sit. Giving it a little more room, and trading a smaller size to keep the same risk, is often more robust.

## Structure: BOS and CHoCH

SMC describes trends with two terms:

- **Break of structure (BOS):** a break in the direction of the trend, such as a new higher high in an uptrend. It suggests continuation.
- **Change of character (CHoCH):** the first break against the trend, such as a close below the last higher low in an uptrend. It warns that the trend may be changing.

These are the same higher-high and lower-low ideas covered in [Trendlines, Channels and Market Structure](/academy/article/trendlines-channels-market-structure), with new labels.

## Order blocks

An **order block** is usually defined as the last opposite-colored candle before a strong move that breaks structure. A bullish order block is the last down candle before a sharp rally; a bearish one is the last up candle before a sharp drop. Traders mark that candle's range as a zone and watch for price to return to it.

The difficulty is that "strong move" and "last candle" leave room for judgment. On any chart there are many candidates, and the one that worked is easy to spot afterwards.

## Fair value gaps

A **fair value gap (FVG)** is a three-candle pattern. In a fast rally, the high of the first candle sits below the low of the third, so the middle candle moved through that price range without any overlap. The empty range is treated as an imbalance that price may return to "fill". A bearish FVG is the mirror image.

Gaps like this appear constantly on low timeframes. Some are revisited, some are not, and some are filled and then broken straight through.

## Premium and discount

Some SMC traders measure the latest swing range and split it at the 50% point. The upper half is the **premium** zone, where they prefer to sell; the lower half is the **discount** zone, where they prefer to buy. It is the same midpoint traders use in [Fibonacci Retracements and Extensions](/academy/article/fibonacci-retracements-extensions).

<CompareTable caption="SMC terms and their closest classic ideas">

| SMC term | Plain-English meaning | Closest classic idea |
| --- | --- | --- |
| Liquidity | Orders clustered beyond obvious highs and lows | Stop clusters |
| Liquidity sweep | A quick move beyond a high or low that reverses | Failed breakout |
| BOS / CHoCH | A break with the trend / the first break against it | Trend continuation / structure break |
| Order block | The last opposing candle before a strong move | Supply or demand zone |
| Fair value gap | A price range a fast move left without overlap | Imbalance, or a gap |
| Premium / discount | The upper or lower half of a range | The 50% retracement |

</CompareTable>

## A worked setup

<Example title="A fair value gap on ETH/USDT (illustrative prices)">

- **Sweep.** On the 1h chart, price dips below an obvious low at 2,980 to 2,965, then closes back above 2,980.
- **Displacement.** A sharp rally follows. Candle one has a high of 3,010, candle two runs up strongly, and candle three has a low of 3,040. The fair value gap is **3,010 to 3,040**.
- **Entry.** A Buy Limit at 3,040, the top of the gap.
- **Stop.** 2,995, below the gap with a little room. Risk per ETH: 3,040 − 2,995 = **45**.
- **Target.** The prior swing high at 3,130. Reward per ETH: 3,130 − 3,040 = **90**, a 2 to 1 ratio.
- **Size.** With 45 USDT at risk: 45 ÷ 45 = **1 ETH**, a position worth 3,040 USDT. At 10x leverage the margin is 3,040 ÷ 10 = **304 USDT**.

</Example>

Three outcomes are possible. Price returns to the gap and rallies to the target, for about +90 USDT before costs. Price never comes back, so the order never fills. Or price slices through the gap and hits the stop, for about −45 USDT plus costs, and more if the stop slips.

On {{BRAND}}, you would place this on the **Limit / Stop** tab: a buy price below the Ask becomes a **Buy Limit**. Turn on **TP / SL**, enter 2,995 and 3,130, and check the order summary. The trade chart has no drawing tools, so keep your zones as a list of prices or mark them in a separate charting tool. On desktop you can drag the pending order on the chart if your zone changes.

{/* TODO: verify with platform — that the trade chart has no drawing tools, and that dragging pending orders is available on desktop. */}

<ProTip title="Write definitions you can test">

Replace "order block" with a rule a stranger could apply, such as "on the 1h chart, the last down candle before a rally that closes above the previous swing high". Then test it on past data and in a demo account, as covered in [Backtesting and Forward-Testing a Strategy](/academy/article/backtesting-forward-testing).

</ProTip>

## The honest limits

- **Not proven.** Popularity is not evidence. There is no robust independent research showing SMC outperforms other ways of reading a chart.
- **Subjective and easy to fit afterwards.** Example charts are chosen once the result is known. In real time, there are several plausible order blocks and gaps.
- **A story that explains everything.** If every loss becomes "the smart money hunted my stop", the framework can never be wrong, and you stop learning from your journal.
- **CFD mechanics.** Your prices follow a reference price from the underlying market. Your own stop on a CFD is not visible in that market's order book.

<Warning>

No chart framework removes risk. Zones fail, gaps get broken and stops can fill at worse prices in fast markets. Leverage magnifies every loss, and many traders lose money. Use a stop on every trade, keep risk per trade small, and judge any SMC rules over many trades, not a handful of clean examples.

</Warning>

## Conclusion

Smart money concepts give names to things traders have watched for decades: stop clusters beyond obvious highs and lows, failed breakouts, structure breaks, zones where strong moves began, and fast moves that left gaps. The framework can sharpen how you think about where to place stops and entries. It is also subjective, unproven and easy to fit after the fact. If you use it, write precise definitions, test them, size from your stop and keep a journal that is allowed to prove you wrong.
